// Windows, shutters, balconies, doors, flags and shopfronts on every façade.
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { PI } from '../lib/math.js';
import { pick, rr } from '../lib/random.js';
import { M, P, canvasTex } from '../lib/factory.js';
import { instanced } from '../lib/instancing.js';
import { textTex } from '../textures/signs.js';
import { grime, stucco } from '../materials/walls.js';
import { roof, shops, wins } from './eixample.js';

export let mergeAny, tint, litGlass, shopGlass;

export function buildFacadeDetails() {
  tint = function tint(g, hex) {
    const c = new THREE.Color(hex),
      n = g.attributes.position.count,
      a = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) a.set([c.r, c.g, c.b], i * 3);
    g.setAttribute('color', new THREE.BufferAttribute(a, 3));
    return g;
  };

  // ---------- street furniture ----------

  // façade details: window frames, glass (some lit at night), shutters, iron balconies, shopfronts
  // RoundedBoxGeometry is non-indexed, so mixed merges need every part non-indexed
  mergeAny = (gs) => mergeGeometries(gs.map((g) => (g.index ? g.toNonIndexed() : g)));
  const placeWin = (o, w) => {
    o.position.set(w.x, w.y, w.z);
    o.rotation.y = w.ry;
  };
  instanced(
    mergeGeometries([
      new THREE.BoxGeometry(0.62, 0.74, 0.04),
      new THREE.BoxGeometry(0.72, 0.045, 0.12).translate(0, -0.39, 0.05),
      new THREE.BoxGeometry(0.72, 0.07, 0.07).translate(0, 0.41, 0.03),
    ]),
    M(0xf3ecdd, { flatShading: false }),
    wins,
    placeWin,
  );
  // roll-down "persianes" half lowered over many windows
  const slatTex = canvasTex(32, 32, (c) => {
    c.fillStyle = '#fff';
    c.fillRect(0, 0, 32, 32);
    c.fillStyle = '#b5b5b5';
    for (let y = 0; y < 32; y += 4) c.fillRect(0, y, 32, 1);
  });
  slatTex.repeat.set(1, 4);
  instanced(
    new THREE.BoxGeometry(0.5, 0.6, 0.02),
    M(0xffffff, { map: slatTex, flatShading: false }),
    wins.filter((w) => w.roll),
    (o, w) => {
      o.position.set(
        w.x + Math.sin(w.ry) * 0.05,
        w.y + 0.3 - w.roll / 2,
        w.z + Math.cos(w.ry) * 0.05,
      );
      o.rotation.y = w.ry;
      o.scale.set(1, w.roll / 0.6, 1);
    },
    [0x5f7d55, 0x6e8c60, 0x7a5638, 0xd8cbb0, 0x4f6a4a],
  );
  // air-conditioning units and balcony flower pots
  const acG = mergeAny([
    tint(new RoundedBoxGeometry(0.34, 0.25, 0.16, 2, 0.02), 0xdedfdb),
    tint(
      new THREE.CylinderGeometry(0.08, 0.08, 0.01, 16).rotateX(PI / 2).translate(-0.05, 0, 0.081),
      0x55585c,
    ),
  ]);
  instanced(
    acG,
    M(0xffffff, { vertexColors: true, flatShading: false }),
    wins.filter((w) => w.ac),
    (o, w) => {
      o.position.set(
        w.x + Math.cos(w.ry) * 0.62 + Math.sin(w.ry) * 0.1,
        w.y - 0.18,
        w.z - Math.sin(w.ry) * 0.62 + Math.cos(w.ry) * 0.1,
      );
      o.rotation.y = w.ry;
    },
  );
  const potG = mergeGeometries([
    tint(
      new THREE.CylinderGeometry(0.06, 0.045, 0.1, 10).translate(0, 0.05, 0).toNonIndexed(),
      0xb5603a,
    ),
    tint(new THREE.IcosahedronGeometry(0.085, 1).translate(0, 0.15, 0), 0x4f8f3a),
  ]);
  const potList = [];
  wins
    .filter((w) => w.b && w.pots)
    .forEach((w) => {
      for (const u of [-0.25, 0.22]) potList.push({ w, u });
    });
  instanced(
    potG,
    M(0xffffff, { vertexColors: true, flatShading: false }),
    potList,
    (o, { w, u }) => {
      o.position.set(
        w.x + Math.cos(w.ry) * u + Math.sin(w.ry) * 0.2,
        w.y - 0.34,
        w.z - Math.sin(w.ry) * u + Math.cos(w.ry) * 0.2,
      );
      o.rotation.y = w.ry;
    },
  );
  // rooms seen through the glass: sheer curtains, a lamp or shelves, in a few variants
  const roomTex = [0, 1, 2, 3].map((v) =>
    canvasTex(64, 128, (c) => {
      const g = c.createLinearGradient(0, 0, 0, 128);
      g.addColorStop(0, ['#3a3530', '#2f3540', '#3b332c', '#343a36'][v]);
      g.addColorStop(1, '#1a1a1d');
      c.fillStyle = g;
      c.fillRect(0, 0, 64, 128);
      if (v === 0) {
        c.fillStyle = '#f3dfb0';
        c.beginPath();
        c.arc(40, 30, 7, 0, PI * 2);
        c.fill();
        c.fillStyle = '#2a2420';
        c.fillRect(39, 37, 2, 40);
      }
      if (v === 1) {
        c.fillStyle = '#4a4038';
        for (const y of [40, 64, 88]) c.fillRect(14, y, 36, 3);
        c.fillStyle = '#8a6a4a';
        for (let x = 16; x < 48; x += 5) c.fillRect(x, 28, 3, 11);
      }
      if (v === 2) {
        c.fillStyle = '#5a4a3c';
        c.fillRect(8, 80, 48, 30);
        c.fillStyle = '#c9a66b';
        c.fillRect(24, 50, 16, 20);
      }
      const curtain = ['#efe6d4', '#e9eef2', '#d9c7a8', '#f2ece2'][v];
      for (const [x0, w] of [
        [0, 18],
        [46, 18],
      ]) {
        c.fillStyle = curtain;
        c.globalAlpha = 0.85;
        c.fillRect(x0, 0, w, 128);
        c.globalAlpha = 0.25;
        c.fillStyle = '#000';
        for (let x = x0 + 3; x < x0 + w; x += 5) c.fillRect(x, 0, 1.5, 128);
        c.globalAlpha = 1;
      }
      c.fillStyle = curtain;
      c.globalAlpha = 0.9;
      c.fillRect(0, 0, 64, 8);
      c.globalAlpha = 1;
      // sky caught in the pane: cool tint fading down, one soft diagonal glint
      const sky = c.createLinearGradient(0, 0, 0, 128);
      sky.addColorStop(0, 'rgba(190,215,240,.42)');
      sky.addColorStop(0.55, 'rgba(160,190,220,.12)');
      sky.addColorStop(1, 'rgba(160,190,220,.05)');
      c.fillStyle = sky;
      c.fillRect(0, 0, 64, 128);
      c.fillStyle = 'rgba(255,255,255,.16)';
      c.beginPath();
      c.moveTo(10 + v * 6, 0);
      c.lineTo(26 + v * 6, 0);
      c.lineTo(-6 + v * 6, 128);
      c.lineTo(-22 + v * 6, 128);
      c.fill();
      // two-leaf casement: painted timber frame, centre meeting rail and glazing bars
      c.fillStyle = '#ece4d2';
      c.fillRect(0, 0, 64, 3);
      c.fillRect(0, 125, 64, 3);
      c.fillRect(0, 0, 3, 128);
      c.fillRect(61, 0, 3, 128);
      c.fillRect(30, 0, 4, 128);
      for (const y of [43, 85]) c.fillRect(0, y, 64, 2);
    }),
  );
  roomTex.forEach((t) => (t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping));
  const darkGlass = roomTex.map((t) =>
    M(0xb4bcc8, {
      map: t,
      roughness: 0.08,
      metalness: 0.35,
      envMapIntensity: 0.9,
      flatShading: false,
    }),
  );
  litGlass = {
    set emissiveIntensity(v) {
      litGlassMats.forEach((m) => (m.emissiveIntensity = v));
    },
  };
  const litGlassMats = roomTex.map((t) =>
    M(0xc8ccd2, {
      map: t,
      emissiveMap: t,
      emissive: 0xffd9a0,
      emissiveIntensity: 0,
      roughness: 0.08,
      metalness: 0.35,
      envMapIntensity: 0.9,
      flatShading: false,
    }),
  );
  wins.forEach((w, i) => (w.room = (i * 7 + Math.floor(w.y * 3)) % 4));
  // windows that open onto a balcony become tall French doors ("balconeres")
  const winG = new THREE.BoxGeometry(0.46, 0.6, 0.06),
    doorG = new THREE.BoxGeometry(0.46, 0.8, 0.06).translate(0, -0.1, 0);
  for (let v = 0; v < 4; v++)
    for (const lit of [false, true])
      for (const tall of [false, true]) {
        const list = wins.filter((w) => w.room === v && !!w.lit === lit && !!w.b === tall);
        if (list.length)
          instanced(tall ? doorG : winG, (lit ? litGlassMats : darkGlass)[v], list, placeWin);
      }
  instanced(
    mergeGeometries([
      new THREE.BoxGeometry(0.16, 0.64, 0.03).translate(-0.4, 0, 0.02),
      new THREE.BoxGeometry(0.16, 0.64, 0.03).translate(0.4, 0, 0.02),
    ]),
    M(0xffffff, { flatShading: false }),
    wins.filter((w) => w.sh),
    placeWin,
    [0x6f8f6a, 0x5d7d5a, 0x8a6a4a, 0x7b8f9a],
  );
  const railTex = canvasTex(128, 64, (c) => {
    c.strokeStyle = '#26282a';
    c.lineWidth = 4;
    c.beginPath();
    c.moveTo(0, 4);
    c.lineTo(128, 4);
    c.moveTo(0, 58);
    c.lineTo(128, 58);
    c.stroke();
    c.lineWidth = 2.5;
    for (let x = 6; x < 128; x += 11) {
      c.beginPath();
      c.moveTo(x, 4);
      c.lineTo(x, 58);
      c.stroke();
    }
    c.lineWidth = 2;
    c.beginPath();
    for (let x = 0; x < 128; x += 22) {
      c.moveTo(x, 40);
      c.quadraticCurveTo(x + 11, 22, x + 22, 40);
    }
    c.stroke();
  });
  railTex.wrapS = railTex.wrapT = THREE.ClampToEdgeWrapping;
  instanced(
    new THREE.BoxGeometry(0.86, 0.04, 0.3).translate(0, -0.36, 0.15),
    M(0xe9e1d2, { flatShading: false }),
    wins.filter((w) => w.b),
    placeWin,
  );
  instanced(
    mergeGeometries([
      new THREE.PlaneGeometry(0.88, 0.24).translate(0, -0.22, 0.3),
      new THREE.PlaneGeometry(0.3, 0.24).rotateY(PI / 2).translate(-0.43, -0.22, 0.15),
      new THREE.PlaneGeometry(0.3, 0.24).rotateY(PI / 2).translate(0.43, -0.22, 0.15),
    ]),
    M(0xffffff, {
      map: railTex,
      alphaTest: 0.5,
      side: THREE.DoubleSide,
      flatShading: false,
      roughness: 0.5,
      metalness: 0.4,
    }),
    wins.filter((w) => w.b),
    placeWin,
  );
  const placeShop = (o, s) => {
    o.position.set(s.x, 0.78, s.z);
    o.rotation.y = s.ry;
  };
  const doors = shops.filter((s) => s.door),
    shopsOnly = shops.filter((s) => !s.door);
  instanced(new THREE.BoxGeometry(1.08, 0.82, 0.05), M(0x3a2f28), shopsOnly, placeShop);
  // building entrances: carved wooden double doors in a stone frame, with a fanlight
  const doorTex = canvasTex(64, 96, (c) => {
    c.fillStyle = '#5a3b26';
    c.fillRect(0, 0, 64, 96);
    c.fillStyle = '#4a2f1e';
    for (const x of [4, 34]) for (const y of [6, 40, 70]) c.fillRect(x, y, 26, y === 40 ? 26 : 20);
    c.fillStyle = '#2a1a10';
    c.fillRect(31, 0, 2, 96);
    c.fillStyle = '#c9a441';
    c.fillRect(27, 50, 3, 6);
    c.fillRect(34, 50, 3, 6);
  });
  doorTex.wrapS = doorTex.wrapT = THREE.ClampToEdgeWrapping;
  instanced(
    new THREE.BoxGeometry(1.12, 1.02, 0.07),
    M(0xd9cfbd, { flatShading: false }),
    doors,
    (o, d) => {
      o.position.set(d.x, 0.87, d.z);
      o.rotation.y = d.ry;
    },
  );
  instanced(
    new THREE.BoxGeometry(0.78, 0.82, 0.09),
    [
      M(0x4a2f1e),
      M(0x4a2f1e),
      M(0x4a2f1e),
      M(0x4a2f1e),
      M(0xffffff, { map: doorTex, flatShading: false }),
      M(0x4a2f1e),
    ],
    doors,
    (o, d) => {
      o.position.set(d.x, 0.77, d.z);
      o.rotation.y = d.ry;
    },
  );
  instanced(
    new THREE.CylinderGeometry(0.39, 0.39, 0.1, 20, 1, false, -PI / 2, PI).rotateX(PI / 2),
    P(0x2a3442, { roughness: 0.05, metalness: 0.5 }),
    doors,
    (o, d) => {
      o.position.set(d.x, 1.18, d.z);
      o.rotation.y = d.ry;
    },
  ).castShadow = false;
  // Catalan senyeres and trailing plants hanging from some balconies
  const senyera = canvasTex(64, 96, (c) => {
    for (let i = 0; i < 9; i++) {
      c.fillStyle = i % 2 ? '#d52b1e' : '#f6c315';
      c.fillRect((i * 64) / 9, 0, 64 / 9 + 0.5, 96);
    }
  });
  senyera.wrapS = senyera.wrapT = THREE.ClampToEdgeWrapping;
  const flagG = new THREE.PlaneGeometry(0.5, 0.78, 6, 8);
  {
    const fp = flagG.attributes.position;
    for (let i = 0; i < fp.count; i++)
      fp.setZ(i, Math.sin(fp.getX(i) * 9 + fp.getY(i) * 3) * 0.025);
    flagG.computeVertexNormals();
  }
  instanced(
    flagG,
    M(0xffffff, { map: senyera, side: THREE.DoubleSide, flatShading: false, roughness: 0.8 }),
    wins.filter((w) => w.b && w.flag),
    (o, w) => {
      o.position.set(w.x + Math.sin(w.ry) * 0.335, w.y - 0.52, w.z + Math.cos(w.ry) * 0.335);
      o.rotation.y = w.ry;
    },
  );
  instanced(
    mergeGeometries([
      new THREE.IcosahedronGeometry(0.1, 1).scale(1.5, 1, 0.6),
      new THREE.IcosahedronGeometry(0.07, 1).translate(-0.12, -0.12, 0),
      new THREE.IcosahedronGeometry(0.06, 1).translate(0.1, -0.2, 0),
      new THREE.IcosahedronGeometry(0.05, 1).translate(0.02, -0.3, 0),
    ]),
    M(0x6aa84f, { flatShading: true, roughness: 0.8 }),
    wins.filter((w) => w.b && w.hang),
    (o, w) => {
      o.position.set(w.x + Math.sin(w.ry) * 0.32, w.y - 0.42, w.z + Math.cos(w.ry) * 0.32);
      o.rotation.y = w.ry;
    },
    [0x7cb35a, 0x8fbf63, 0x6aa84f, 0xb5d07a],
  );
  // shop interiors seen through the glass, one kind of shop per variant: bookshop, bakery (forn), greengrocer,
  // café-bar, clothes shop, pharmacy. Each has its own back wall, goods, counter and a glint on the pane.
  const shopTex = [0, 1, 2, 3, 4, 5].map((v) =>
    canvasTex(256, 192, (c) => {
      const wall = ['#5a4a3c', '#7a5a3a', '#4f5a3c', '#4a3a30', '#e8e2d8', '#f2f2ee'][v],
        floor = ['#2a221c', '#3a2a1c', '#2a2a1c', '#2a1e18', '#a89c8a', '#c8ccc8'][v];
      const g = c.createLinearGradient(0, 0, 0, 192);
      g.addColorStop(0, wall);
      g.addColorStop(0.75, floor);
      g.addColorStop(1, floor);
      c.fillStyle = g;
      c.fillRect(0, 0, 256, 192);
      c.fillStyle = 'rgba(255,240,210,.9)';
      for (const x of [50, 128, 206]) {
        c.fillRect(x - 14, 6, 28, 4);
      } // ceiling lights
      const R = (col, x, y, w, h) => {
          c.fillStyle = col;
          c.fillRect(x, y, w, h);
        },
        O = (col, x, y, rx, ry) => {
          c.fillStyle = col;
          c.beginPath();
          c.ellipse(x, y, rx, ry, 0, 0, PI * 2);
          c.fill();
        };
      if (v === 0) {
        // books
        for (const y of [44, 84, 124]) {
          R('#6e5a46', 10, y, 236, 5);
          for (let x = 12; x < 240; x += rr(7, 12)) {
            const hh = rr(14, 30);
            R(
              pick(['#d9534f', '#f0c419', '#3b7dd8', '#2e9e6a', '#f2f0ea', '#e67e22', '#7a3b5a']),
              x,
              y - hh,
              rr(5, 9),
              hh,
            );
          }
        }
        R('#3a2c22', 150, 140, 96, 52);
        R('#9a8670', 150, 136, 96, 5);
        for (let x = 20; x < 130; x += 22)
          R(pick(['#c94a3a', '#2e6e9e', '#e0a526']), x, 150, 16, 22);
      }
      if (v === 1) {
        // bread on wicker shelves, glass counter of pastries
        for (const y of [50, 90]) {
          R('#a07848', 10, y, 236, 6);
          for (let x = 22; x < 240; x += rr(18, 26))
            O(pick(['#d9a35a', '#c98a42', '#e8c07a', '#b87a3a']), x, y - 8, rr(8, 12), rr(5, 8));
        }
        for (let x = 20; x < 236; x += 30) {
          R('#c98a42', x, 30, 4, 22);
          R('#d9a35a', x + 6, 26, 4, 26);
        }
        R('#e8e0d0', 10, 130, 236, 62);
        R('rgba(200,225,240,.6)', 14, 112, 228, 20);
        for (let x = 22; x < 236; x += 16)
          O(pick(['#f2d08a', '#8a4a2a', '#f0e6d0', '#d9534f']), x, 126, 6, 4);
      }
      if (v === 2) {
        // fruit crates stepping down to the window
        R('#3a4a2a', 0, 30, 256, 10);
        for (let row = 0; row < 3; row++) {
          const y = 70 + row * 40;
          for (let x = 6; x < 250; x += 48) {
            R('#7a5a32', x, y + 18, 44, 14);
            const fruit = pick(['#e03a2a', '#f0a020', '#f2d43a', '#6ab03a', '#8a2a5a', '#f07a2a']);
            for (let k = 0; k < 6; k++) O(fruit, x + 6 + k * 7, y + 14 + (k % 2) * 2, 5, 5);
          }
        }
        for (let x = 30; x < 256; x += 60) {
          R('#2a3a1a', x, 14, 2, 30);
          O('#f2d43a', x + 1, 46, 8, 6);
        }
      }
      if (v === 3) {
        // bar: bottles on a back shelf, counter, small tables and lamps
        for (const y of [40, 70]) {
          R('#5a4030', 120, y, 130, 4);
          for (let x = 124; x < 246; x += 9)
            R(pick(['#2a6a3a', '#7a3a1a', '#c9b05a', '#e8e8e0', '#3a2a5a']), x, y - 18, 6, 18);
        }
        R('#6a4a32', 120, 100, 136, 92);
        R('#c9a46a', 120, 96, 136, 6);
        R('#d8d0c0', 140, 80, 10, 16);
        R('#b0a890', 170, 84, 26, 12);
        for (const x of [30, 80]) {
          R('#2a1e18', x - 2, 120, 4, 50);
          O('#3a2a20', x, 120, 22, 5);
          R('#1e1612', x - 30, 140, 4, 40);
          R('#1e1612', x + 26, 140, 4, 40);
        }
        for (const x of [40, 90]) {
          R('#1a1410', x, 0, 1, 40);
          O('#f2c060', x, 44, 9, 6);
        }
      }
      if (v === 4) {
        // clothes on a rail, a mannequin, folded stacks
        R('#9a948a', 10, 34, 150, 3);
        for (let x = 16; x < 156; x += 13) {
          const col = pick([
            '#2f3c55',
            '#c2457a',
            '#f2f0ea',
            '#3b7dd8',
            '#7a8f5a',
            '#d9534f',
            '#1f1f22',
          ]);
          R('#5a5a5a', x + 5, 34, 1, 8);
          R(col, x, 42, 12, 48);
        }
        O('#d8cbbc', 205, 40, 9, 10);
        R('#2a2a2e', 190, 52, 30, 50);
        R('#2f3c55', 193, 102, 11, 50);
        R('#2f3c55', 206, 102, 11, 50);
        for (let x = 14; x < 170; x += 32)
          for (let k = 0; k < 4; k++)
            R(pick(['#c9b99a', '#2f3c55', '#8a2c35', '#e8e6e0']), x, 160 - k * 7, 26, 6);
        R('#c8c0b4', 10, 168, 160, 24);
      }
      if (v === 5) {
        // pharmacy: white shelving of small boxes, green cross, counter
        for (const y of [40, 70, 100]) {
          R('#d8dcd8', 8, y, 240, 4);
          for (let x = 10; x < 246; x += rr(7, 11)) {
            const hh = rr(10, 20);
            R(
              pick(['#ffffff', '#3a9a5a', '#e04a3a', '#3b7dd8', '#f0c419', '#e8e8f0']),
              x,
              y - hh,
              rr(5, 9),
              hh,
            );
          }
        }
        R('#2eaa5a', 116, 6, 24, 8);
        R('#2eaa5a', 124, -2, 8, 24);
        R('#ffffff', 60, 136, 136, 56);
        R('#3a9a5a', 60, 132, 136, 6);
      }
      // the pane: reflected sky at the top and a soft diagonal glint
      const sky = c.createLinearGradient(0, 0, 0, 192);
      sky.addColorStop(0, 'rgba(200,222,242,.35)');
      sky.addColorStop(0.4, 'rgba(200,222,242,.08)');
      sky.addColorStop(1, 'rgba(0,0,0,0)');
      c.fillStyle = sky;
      c.fillRect(0, 0, 256, 192);
      c.fillStyle = 'rgba(255,255,255,.12)';
      c.beginPath();
      c.moveTo(60 + v * 20, 0);
      c.lineTo(100 + v * 20, 0);
      c.lineTo(20 + v * 20, 192);
      c.lineTo(-20 + v * 20, 192);
      c.fill();
    }),
  );
  shopTex.forEach((t) => (t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping));
  const shopGlassMats = shopTex.map((t) =>
    M(0xffffff, {
      map: t,
      emissiveMap: t,
      emissive: 0xffffff,
      roughness: 0.05,
      metalness: 0.2,
      envMapIntensity: 1.2,
      emissiveIntensity: 0.05,
      flatShading: false,
    }),
  );
  shopGlass = {
    set emissiveIntensity(v) {
      shopGlassMats.forEach((m) => (m.emissiveIntensity = v));
    },
  };
  shopGlassMats.forEach((m, v) => {
    const list = shopsOnly.filter((_, i) => (((i * 5 + Math.floor(_.x * 3)) % 6) + 6) % 6 === v);
    if (list.length) instanced(new THREE.BoxGeometry(0.9, 0.66, 0.07), m, list, placeShop);
  });
  instanced(
    new THREE.BoxGeometry(1.1, 0.06, 0.42).translate(0, 0, 0.21),
    M(0xffffff),
    shops.filter((s) => s.awning),
    (o, s) => {
      o.position.set(s.x, 1.3, s.z);
      o.rotation.y = s.ry;
      o.rotateX(0.35);
    },
    [0xc8463a, 0x2e6e9e, 0x3f8a58, 0xe0a526, 0x8c3b6b, 0xf2efe6],
  );
  {
    const at = (o, r) => {
      o.position.set(r.x, r.y, r.z);
      if (r.r !== undefined) o.rotation.y = r.r;
    };
    instanced(
      new RoundedBoxGeometry(1.4, 1.1, 1.8, 2, 0.05).translate(0, 0.55, 0),
      grime(M(0xe2d6c2, { flatShading: false, map: stucco })),
      roof.huts,
      at,
    );
    // light wells ("celoberts"): a dark shaft opening in the roof behind a low parapet
    instanced(
      new THREE.PlaneGeometry(1.3, 1.7).rotateX(-PI / 2).translate(0, 0.03, 0),
      M(0x4a443e, { roughness: 1 }),
      roof.wells,
      at,
    ).castShadow = false;
    instanced(
      mergeGeometries(
        [
          [0, 0.89],
          [0, -0.89],
          [0.69, 0],
          [-0.69, 0],
        ].map(([x, z]) =>
          new THREE.BoxGeometry(x ? 0.08 : 1.46, 0.28, x ? 1.86 : 0.08).translate(x, 0.14, z),
        ),
      ),
      M(0xe9e1d2, { flatShading: false }),
      roof.wells,
      at,
    );
    instanced(
      mergeAny([
        new THREE.CylinderGeometry(0.36, 0.36, 0.75, 20).translate(0, 0.62, 0),
        new THREE.CylinderGeometry(0.04, 0.04, 0.25, 6).translate(0.2, 0.12, 0.2),
        new THREE.CylinderGeometry(0.04, 0.04, 0.25, 6).translate(-0.2, 0.12, -0.2),
      ]),
      P(0xd9dbdc, { roughness: 0.4, metalness: 0.6 }),
      roof.tanks,
      at,
    );
    instanced(
      mergeAny([
        new THREE.CylinderGeometry(0.02, 0.025, 1.8, 6).translate(0, 0.9, 0),
        new THREE.BoxGeometry(0.9, 0.02, 0.02).translate(0, 1.5, 0),
        new THREE.BoxGeometry(0.6, 0.02, 0.02).translate(0, 1.7, 0),
        new THREE.BoxGeometry(0.02, 0.02, 0.5).translate(0.3, 1.5, 0),
      ]),
      P(0x9ea3a8, { roughness: 0.4, metalness: 0.8 }),
      roof.antennas,
      at,
    );
    instanced(
      mergeAny([
        new THREE.SphereGeometry(0.3, 20, 10, 0, PI * 2, 0, 0.9)
          .scale(1, 0.45, 1)
          .rotateX(-1.1)
          .translate(0, 0.55, 0),
        new THREE.CylinderGeometry(0.025, 0.025, 0.5, 6).translate(0, 0.25, 0),
      ]),
      P(0xeeeeec, { roughness: 0.35, metalness: 0.3, side: THREE.DoubleSide }),
      roof.dishes,
      at,
    );
    const pvTex = textTex(64, 64, (c) => {
      c.fillStyle = '#1d2a44';
      c.fillRect(0, 0, 64, 64);
      c.strokeStyle = '#8a97b0';
      c.lineWidth = 1;
      for (let i = 0; i <= 64; i += 16) {
        c.beginPath();
        c.moveTo(i, 0);
        c.lineTo(i, 64);
        c.moveTo(0, i);
        c.lineTo(64, i);
        c.stroke();
      }
    });
    instanced(
      new THREE.BoxGeometry(0.7, 0.03, 1.1).rotateX(-0.45).translate(0, 0.45, 0),
      [
        P(0x8a8f96),
        P(0x8a8f96),
        P(0xffffff, { map: pvTex, roughness: 0.15, metalness: 0.6 }),
        P(0x8a8f96),
        P(0x8a8f96),
        P(0x8a8f96),
      ],
      roof.panels,
      at,
    );
    instanced(
      mergeAny([
        new THREE.CylinderGeometry(0.12, 0.12, 0.5, 12).translate(0, 0.25, 0),
        new THREE.CylinderGeometry(0.2, 0.2, 0.06, 12).translate(0, 0.53, 0),
      ]),
      P(0x8f9399, { roughness: 0.45, metalness: 0.6 }),
      roof.vents,
      at,
    );
    const clothes = [];
    for (const sx of [-1, 1])
      clothes.push(
        tint(
          new THREE.CylinderGeometry(0.025, 0.025, 1.2, 6).translate(sx * 1.1, 0.6, 0),
          0x9ea3a8,
        ),
      );
    clothes.push(
      tint(
        new THREE.CylinderGeometry(0.006, 0.006, 2.2, 4).rotateZ(PI / 2).translate(0, 1.15, 0),
        0xdddddd,
      ),
    );
    [0xd9534f, 0xf2f0ea, 0x3b7dd8, 0xf0c419, 0x2e9e6a, 0xffffff, 0xc2457a].forEach((col, k) =>
      clothes.push(
        tint(
          new THREE.PlaneGeometry(rr(0.18, 0.3), rr(0.25, 0.45)).translate(
            -0.9 + k * 0.3,
            1.15 - 0.18,
            0,
          ),
          col,
        ),
      ),
    );
    instanced(
      mergeAny(clothes),
      M(0xffffff, { vertexColors: true, side: THREE.DoubleSide, flatShading: false }),
      roof.laundry,
      at,
    );
    instanced(
      mergeAny([
        tint(new THREE.BoxGeometry(0.4, 0.9, 0.4).translate(0, 0.45, 0), 0xb06a4a),
        tint(new THREE.BoxGeometry(0.5, 0.08, 0.5).translate(0, 0.94, 0), 0xd9cfbd),
        tint(new THREE.CylinderGeometry(0.08, 0.08, 0.2, 8).translate(0, 1.08, 0), 0x8a8f96),
      ]),
      M(0xffffff, { vertexColors: true, flatShading: false }),
      roof.chimneys,
      at,
    );
  }
}
